import { compact, zip } from 'lodash'
import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { TagProps } from '../components/app/design/Tag'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { mergeFk } from '../utils'
import { BookableColorKey, getDefaultServiceColorKey } from '../utils/calendarColors'
import { formatTimeStamp } from '../utils/dates'
import { currencyFloat } from '../utils/numbers'
import { Allocation, AllocationConstructorArgs } from './Allocation'
import { BaseRecord } from './BaseRecord'
import { Bookable, ScheduleState } from './Bookable'
import { Customizable } from './Customizable'
import { Estimate } from './Estimate'
import { Field } from './Field'
import { Gift } from './Gift'
import { Industry } from './Industry'
import { Item } from './Item'
import { Job, JobConstructorArgs } from './Job'
import { Lesson } from './Lesson'
import { Line } from './Line'
import { Mappable } from './Mappable'
import { Option } from './Option'
import { PSEvent } from './PSEvent'
import { Package } from './Package'
import { Payment } from './Payment'
import { Person } from './Person'
import { Promotion } from './Promotion'
import { RecordDataMap } from './RecordDataMap'
import { RecordFactory } from './RecordFactory'
import { Resource } from './Resource'
import { FKType } from './SQLBuilder'
import { Schedule } from './Schedule'
import { Source } from './Source'
import { Subaccount } from './Subaccount'
import { Transactable } from './Transactable'
import { Transaction } from './Transaction'
import { User } from './User'

type BookingConstructorArgs = Omit<
    ConstructorArgs<() => Booking>,
    'dropin' | 'online' | 'jobs' | 'allocations'
> & {
    source_display: string | null
    is_dropin: boolean | null
    is_online: boolean | null
    jobs: JobConstructorArgs[] | Job[] | null
    allocations: AllocationConstructorArgs[] | Allocation[] | null
}

export const MAX_HOURLY_DURATION = 840

export type BookingAction =
    | 'VIEW'
    | 'CREATE'
    | 'EDIT'
    | 'EDIT_SERIES'
    | 'CONFIRM'
    | 'SUMMARY'
    | 'COMPLETE'
    | 'UNKNOWN'

export class Booking extends Transaction implements Bookable, Customizable, Mappable, Transactable {
    readonly recordType = 'booking'

    readonly iBookable = true
    readonly iCustomizable = true
    readonly iMappable = true
    readonly iTransactable = true

    static readonly MAX_HOURLY_DURATION = 840

    longitude?: number
    latitude?: number
    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
    countryCode?: string

    fieldValues: RecordDataMap = new RecordDataMap()

    memo?: string
    instructions?: string
    type?: string
    status?: string
    confirmStatus?: string
    private _endDate?: moment.Moment

    private _product?: Item
    dropin?: boolean
    online?: boolean
    event?: PSEvent
    allowSurcharge?: boolean
    allowConference?: boolean
    conferenceType?: string

    rate?: number
    deposit?: number
    number?: number
    duration?: number
    checkout?: string // only set during client checkout (used to avoid double booking)

    rebook?: string
    cancellationType?: string
    cancellationMemo?: string

    dates: moment.Moment[] = [] // only set when booking multiple lessons or client requests (not stored)
    lessons: Lesson[] = [] // only set when booking multiple drop-in classes (not stored)
    employees: User[][] = [] // only set when booking multiple sessions with teams (not stored)
    resources: Allocation[][] = [] // only set when booking multiple sessions with resources (not stored)
    forSchedule?: boolean // YES if this instance is being used to create/edit a series or multiple sessions (not stored)

    autoAssign?: string // set during client checkout to auto-assign in the background
    customer?: User // set during client checkout (package or gift certificate) if purchase is for someone else
    allowGift?: boolean // set during client checkout (package or gift certificate) if purchase is for someone else
    message?: string // set during client checkout (package or gift certificate) if purchase is for someone else

    markPaidDate?: moment.Moment
    invoicedDate?: moment.Moment
    rebookDate?: moment.Moment
    paidDepositDate?: moment.Moment

    checkinDate?: moment.Moment
    checkoutDate?: moment.Moment
    completedDate?: moment.Moment
    confirmedDate?: moment.Moment
    declare protected _lastModifiedDate?: moment.Moment
    packageCancelledDate?: moment.Moment

    reserveCheckinType: 'any' | 'fixed' | 'flex' | null
    reserveCheckinDuration: number | null
    reserveCheckoutType: 'any' | 'fixed' | 'flex' | null
    reserveCheckoutDuration: number | null
    reserveDate: moment.Moment | null
    reserveEndDate: moment.Moment | null

    jobs: Job[] = []
    allocations: Allocation[] = []

    readonly sourceString?: string
    readonly conference?: string
    readonly conferenceUrlString?: string
    readonly graduationString?: string

    constructor(data?: Partial<BookingConstructorArgs>) {
        super(data)
        if (!data) {
            this.channel = 'sms'
            this.reminder = '48'
            this.fieldValues = new RecordDataMap()
            this.reserveCheckinType = null
            this.reserveCheckinDuration = null
            this.reserveCheckoutType = null
            this.reserveCheckoutDuration = null
            this.reserveDate = null
            this.reserveEndDate = null
            return
        }

        this.longitude = this.propertyFromNumberArg(data.longitude)
        this.latitude = this.propertyFromNumberArg(data.latitude)
        this.line1 = data.line1 ?? undefined
        this.line2 = data.line2 ?? undefined
        this.city = data.city ?? undefined
        this.state = data.state ?? undefined
        this.postalCode = data.postal_code ?? undefined
        this.countryCode = data.country_code ?? undefined
        this.date = this.propertyFromTimestampArg(data.date)
        this.dropin = data.is_dropin ?? false
        this.online = data.is_online ?? false
        this.forSchedule = data.for_schedule ?? false
        this.allowSurcharge = data.allow_surcharge ?? false
        this.allowConference = data.allow_conference ?? false
        this.conferenceType = data.conference_type ?? undefined
        if (this.item && this.item.type === 'reservation')
            this._endDate = this.propertyFromTimestampArg(data.end_date)
        this.fieldValues = Field.loadFromRecordMap(
            new RecordDataMap(data),
            !data.vendor
                ? undefined
                : typeof data.vendor === 'string'
                  ? data.vendor
                  : data.vendor.id,
            undefined
        )
        this.rate = this.propertyFromCurrencyArg(data.rate)
        this.deposit = this.propertyFromCurrencyArg(data.deposit)
        this.duration = this.propertyFromIntArg(data.duration)
        this.number = this.propertyFromCurrencyArg(data.number)
        this.memo = data.memo ?? undefined
        this.instructions = data.instructions ?? undefined
        this.type = data.type ?? undefined
        this.status = data.status ?? undefined
        this.rebook = data.rebook ?? undefined
        this.cancellationType = data.cancellation_type ?? undefined
        this.cancellationMemo = data.cancellation_memo ?? undefined
        this.confirmStatus = data.confirm_status ?? undefined
        this.checkout = data.checkout ?? undefined
        this.rebookDate = this.propertyFromTimestampArg(data.rebook_date)
        this.markPaidDate = this.propertyFromTimestampArg(data.mark_paid_date)
        this.invoicedDate = this.propertyFromTimestampArg(data.invoiced_date)
        this.confirmedDate = this.propertyFromTimestampArg(data.confirmed_date)
        this.packageCancelledDate = this.propertyFromTimestampArg(data.package_cancelled_date)
        this.checkinDate = this.propertyFromTimestampArg(data.checkin_date)
        this.checkoutDate = this.propertyFromTimestampArg(data.checkout_date)
        this.reserveCheckinType = data.reserve_checkin_type ?? null
        this.reserveCheckinDuration = this.propertyFromIntArg(data.reserve_checkin_duration) ?? null
        this.reserveCheckoutType = data.reserve_checkout_type ?? null
        this.reserveCheckoutDuration =
            this.propertyFromIntArg(data.reserve_checkout_duration) ?? null
        this.reserveDate = this.propertyFromTimestampArg(data.reserve_date) ?? null
        this.reserveEndDate = this.propertyFromTimestampArg(data.reserve_end_date) ?? null
        this.completedDate = this.propertyFromTimestampArg(data.completed_date)
        this.paidDepositDate = this.propertyFromTimestampArg(data.paid_deposit_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.jobs = (data.jobs ?? []).map((job) => (job instanceof Job ? job : new Job(job)))
        this.allocations = (data.allocations ?? []).map((allocation) =>
            allocation instanceof Allocation ? allocation : new Allocation(allocation)
        )
        this.sourceString = data.source_display ?? undefined
        this.conference = data.conference ?? undefined
        this.conferenceUrlString = data.conference_url_string ?? undefined
        this.graduationString = data.graduation_string ?? undefined
        this.dates = compact((data.dates ?? []).map((date) => this.propertyFromTimestampArg(date)))
        this.lessons = (data.lessons ?? []).map((lesson) =>
            lesson instanceof Lesson ? lesson : new Lesson(lesson)
        )
    }

    clone(overrides?: Partial<BookingConstructorArgs>): Booking {
        return new Booking({
            ...this.serializeForClone(),
            source_display: this.sourceString,
            is_dropin: this.dropin,
            is_online: this.online,
            ...overrides,
        })
    }

    static nextDate(
        date: moment.Moment,
        vendor: User | undefined,
        forVendor: boolean | undefined
    ): moment.Moment {
        const selectedHour = date.hour()
        let dow = date.day() + 1
        dow = dow === 1 ? Math.pow(2, 6) : Math.pow(2, dow - 2)
        let nextAvailableHour = 8
        if (forVendor) nextAvailableHour = selectedHour
        else {
            const days = Schedule.dayStrings()
            const dowString = `${dow}`
            const option = days.find((obj) => obj.id === dowString)
            if (option && vendor) {
                const startHour =
                    option.label !== undefined
                        ? vendor.getStartHourOnDay(option.label.substring(0, 3).toLowerCase())
                        : undefined
                const endHour =
                    option.label !== undefined
                        ? vendor.getEndHourOnDay(option.label.substring(0, 3).toLowerCase())
                        : undefined
                // set the hour to be the selected hour if it falls between the business hours, otherwise default it to 8am
                if (startHour && endHour && selectedHour <= endHour && selectedHour >= startHour) {
                    nextAvailableHour = selectedHour
                }
            }
        }
        return moment(date).hour(nextAvailableHour).minute(0).second(0).millisecond(0)
    }

    static durationStrings(forHours: boolean, forDays: boolean): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: 'TBD' }))
        strings.push(new Option(undefined, { id: '10', label: '10 minutes' }))
        strings.push(new Option(undefined, { id: '15', label: '15 minutes' }))
        strings.push(new Option(undefined, { id: '20', label: '20 minutes' }))
        strings.push(new Option(undefined, { id: '25', label: '25 minutes' }))
        strings.push(new Option(undefined, { id: '30', label: '30 minutes' }))
        strings.push(new Option(undefined, { id: '35', label: '35 minutes' }))
        strings.push(new Option(undefined, { id: '40', label: '40 minutes' }))
        strings.push(new Option(undefined, { id: '45', label: '45 minutes' }))
        strings.push(new Option(undefined, { id: '50', label: '50 minutes' }))
        strings.push(new Option(undefined, { id: '55', label: '55 minutes' }))
        strings.push(new Option(undefined, { id: '60', label: '1 hour' }))
        for (let i = 65; i <= Booking.MAX_HOURLY_DURATION; i += 5) {
            const id = i.toString()
            const hourString = i / 60 < 2 ? '1 hour' : `${Math.floor(i / 60)} hours`
            if (i % 60 > 0) {
                const minuteString = `${i % 60} minutes`
                strings.push(new Option(undefined, { id, label: `${hourString}, ${minuteString}` }))
            }
            else strings.push(new Option(undefined, { id, label: hourString }))
        }

        const dayStrings: Option[] = []
        dayStrings.push(new Option(undefined, { id: '1440', label: '1 day' }))
        dayStrings.push(new Option(undefined, { id: '2880', label: '2 days' }))
        dayStrings.push(new Option(undefined, { id: '4320', label: '3 days' }))
        dayStrings.push(new Option(undefined, { id: '5760', label: '4 days' }))
        dayStrings.push(new Option(undefined, { id: '7200', label: '5 days' }))
        dayStrings.push(new Option(undefined, { id: '8640', label: '6 days' }))
        dayStrings.push(new Option(undefined, { id: '10080', label: '1 week' }))
        dayStrings.push(new Option(undefined, { id: '11520', label: '8 days' }))
        dayStrings.push(new Option(undefined, { id: '12960', label: '9 days' }))
        dayStrings.push(new Option(undefined, { id: '14400', label: '10 days' }))
        dayStrings.push(new Option(undefined, { id: '15840', label: '11 days' }))
        dayStrings.push(new Option(undefined, { id: '17280', label: '12 days' }))
        dayStrings.push(new Option(undefined, { id: '18720', label: '13 days' }))
        dayStrings.push(new Option(undefined, { id: '20160', label: '2 weeks' }))
        dayStrings.push(new Option(undefined, { id: '21600', label: '15 days' }))
        dayStrings.push(new Option(undefined, { id: '23040', label: '16 days' }))
        dayStrings.push(new Option(undefined, { id: '24480', label: '17 days' }))
        dayStrings.push(new Option(undefined, { id: '25920', label: '18 days' }))
        dayStrings.push(new Option(undefined, { id: '27360', label: '19 days' }))
        dayStrings.push(new Option(undefined, { id: '28800', label: '20 days' }))
        dayStrings.push(new Option(undefined, { id: '30240', label: '3 weeks' }))
        dayStrings.push(new Option(undefined, { id: '31680', label: '22 days' }))
        dayStrings.push(new Option(undefined, { id: '33120', label: '23 days' }))
        dayStrings.push(new Option(undefined, { id: '34560', label: '24 days' }))
        dayStrings.push(new Option(undefined, { id: '36000', label: '25 days' }))
        dayStrings.push(new Option(undefined, { id: '37440', label: '26 days' }))
        dayStrings.push(new Option(undefined, { id: '38880', label: '27 days' }))
        dayStrings.push(new Option(undefined, { id: '40320', label: '4 weeks' }))
        dayStrings.push(new Option(undefined, { id: '41760', label: '29 days' }))
        dayStrings.push(new Option(undefined, { id: '43200', label: '30 days' }))
        dayStrings.push(new Option(undefined, { id: '50400', label: '5 weeks' }))
        dayStrings.push(new Option(undefined, { id: '60480', label: '6 weeks' }))
        dayStrings.push(new Option(undefined, { id: '70560', label: '7 weeks' }))
        dayStrings.push(new Option(undefined, { id: '80640', label: '8 weeks' }))
        dayStrings.push(new Option(undefined, { id: '90720', label: '9 weeks' }))
        dayStrings.push(new Option(undefined, { id: '100800', label: '10 weeks' }))

        if (forDays && forHours) {
            strings.push(...dayStrings)
            return strings
        }

        return forDays ? dayStrings : strings
    }

    static bufferTimeStrings(forPayee: boolean): Option[] {
        const strings: Option[] = []
        if (forPayee) {
            strings.push(new Option(undefined, { id: '', label: 'Same as business' }))
            strings.push(new Option(undefined, { id: '0', label: 'None' }))
        }
        else strings.push(new Option(undefined, { id: '', label: 'None' }))
        strings.push(new Option(undefined, { id: '5', label: '5 minutes' }))
        strings.push(new Option(undefined, { id: '10', label: '10 minutes' }))
        strings.push(new Option(undefined, { id: '15', label: '15 minutes' }))
        strings.push(new Option(undefined, { id: '20', label: '20 minutes' }))
        strings.push(new Option(undefined, { id: '25', label: '25 minutes' }))
        strings.push(new Option(undefined, { id: '30', label: '30 minutes' }))
        strings.push(new Option(undefined, { id: '45', label: '45 minutes' }))
        strings.push(new Option(undefined, { id: '60', label: '1 hour' }))
        strings.push(new Option(undefined, { id: '90', label: '90 minutes' }))
        strings.push(new Option(undefined, { id: '120', label: '2 hours' }))
        return strings
    }

    static flexStartTimeStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: 'No Flex' }))
        strings.push(new Option(undefined, { id: 'buffer', label: 'No Flex + buffer' }))
        strings.push(new Option(undefined, { id: '10', label: 'Flex 10 minutes' }))
        strings.push(new Option(undefined, { id: '15', label: 'Flex 15 minutes' }))
        strings.push(new Option(undefined, { id: '20', label: 'Flex 20 minutes' }))
        strings.push(new Option(undefined, { id: '30', label: 'Flex 30 minutes' }))
        strings.push(new Option(undefined, { id: '60', label: 'Flex 60 minutes' }))
        return strings
    }

    static availableRangeStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: 'No limit' }))
        strings.push(new Option(undefined, { id: 'tw', label: 'This week' }))
        strings.push(new Option(undefined, { id: 'tm', label: 'This month' }))
        strings.push(new Option(undefined, { id: '1d', label: '1 day' }))
        strings.push(new Option(undefined, { id: '2d', label: '2 days' }))
        strings.push(new Option(undefined, { id: '3d', label: '3 days' }))
        strings.push(new Option(undefined, { id: '4d', label: '4 days' }))
        strings.push(new Option(undefined, { id: '5d', label: '5 days' }))
        strings.push(new Option(undefined, { id: '6d', label: '6 days' }))
        strings.push(new Option(undefined, { id: '1w', label: '1 week' }))
        strings.push(new Option(undefined, { id: '2w', label: '2 weeks' }))
        strings.push(new Option(undefined, { id: '3w', label: '3 weeks' }))
        strings.push(new Option(undefined, { id: '4w', label: '4 weeks' }))
        strings.push(new Option(undefined, { id: '5w', label: '5 weeks' }))
        strings.push(new Option(undefined, { id: '6w', label: '6 weeks' }))
        strings.push(new Option(undefined, { id: '1m', label: '1 month' }))
        strings.push(new Option(undefined, { id: '2m', label: '2 months' }))
        strings.push(new Option(undefined, { id: '3m', label: '3 months' }))
        strings.push(new Option(undefined, { id: '4m', label: '4 months' }))
        strings.push(new Option(undefined, { id: '5m', label: '5 months' }))
        strings.push(new Option(undefined, { id: '6m', label: '6 months' }))
        return strings
    }

    static channelStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: "Don't notify" }))
        strings.push(new Option(undefined, { id: 'sms', label: 'Text message' }))
        strings.push(new Option(undefined, { id: 'combo', label: 'Text and email' }))
        strings.push(new Option(undefined, { id: 'email', label: 'Email' }))
        return strings
    }

    static reminderStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: "Don't remind" }))
        strings.push(new Option(undefined, { id: '2', label: '2 hours' }))
        strings.push(new Option(undefined, { id: '4', label: '4 hours' }))
        strings.push(new Option(undefined, { id: '6', label: '6 hours' }))
        strings.push(new Option(undefined, { id: '8', label: '8 hours' }))
        strings.push(new Option(undefined, { id: '12', label: '12 hours' }))
        strings.push(new Option(undefined, { id: '24', label: '24 hours' }))
        strings.push(new Option(undefined, { id: '36', label: '36 hours' }))
        strings.push(new Option(undefined, { id: '48', label: '2 days' }))
        strings.push(new Option(undefined, { id: '72', label: '3 days' }))
        return strings
    }

    static cancellationTypeStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'late_cancellation', label: 'Late cancellation' }))
        strings.push(new Option(undefined, { id: 'no_show', label: 'No show' }))
        strings.push(new Option(undefined, { id: 'courtesy', label: 'Courtesy' }))
        strings.push(new Option(undefined, { id: 'reschedule', label: 'Reschedule' }))
        return strings
    }

    static rebookStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: 'None' }))
        strings.push(new Option(undefined, { id: '7d', label: '7 days' }))
        strings.push(new Option(undefined, { id: '14d', label: '14 days' }))
        strings.push(new Option(undefined, { id: '28d', label: '4 weeks' }))
        strings.push(new Option(undefined, { id: '42d', label: '6 weeks' }))
        strings.push(new Option(undefined, { id: '1m', label: '1 month' }))
        strings.push(new Option(undefined, { id: '2m', label: '2 months' }))
        strings.push(new Option(undefined, { id: '3m', label: '3 months' }))
        strings.push(new Option(undefined, { id: '6m', label: '6 months' }))
        strings.push(new Option(undefined, { id: '12m', label: '1 year' }))
        strings.push(new Option(undefined, { id: '24m', label: '2 years' }))
        return strings
    }

    get lines(): Line[] {
        return super.lines
    }

    set lines(lines: Line[]) {
        super.lines = lines
        for (const line of this.lines) {
            line.trandoc = this
        }
    }

    hasChangesFrom(otherBooking: Booking): boolean {
        if (BaseRecord.hasChanges(this, otherBooking)) {
            return true
        }

        return this.hasLineChangesFrom(otherBooking)
    }

    hasLineChangesFrom(otherBooking: Booking): boolean {
        for (const [lhsLine, rhsLine] of zip(this.lines, otherBooking.lines)) {
            if (lhsLine === undefined || rhsLine === undefined) {
                return true
            }
            if (lhsLine.hasChangesFrom(rhsLine)) {
                return true
            }
        }

        return false
    }

    get allowSalesTax(): boolean {
        return this.nexus !== undefined && this.nexus !== ''
    }

    get leadtime(): string | undefined {
        let leadtime: string | undefined = undefined
        let maxLeadtimeAsFloat: number = 0
        for (let item of this.services) {
            let leadtimeAsFloat: number = 0
            if (!isNaN(Number(item.leadtime))) leadtimeAsFloat = Number(item.leadtime)
            else if (item.leadtime === 'nextDay') leadtimeAsFloat = 18
            else if (item.leadtime?.endsWith('m'))
                leadtimeAsFloat = Number(item.leadtime?.substring(0, 2)) / 60
            if (item.leadtime && (!leadtime || leadtimeAsFloat > maxLeadtimeAsFloat)) {
                leadtime = item.leadtime
                maxLeadtimeAsFloat = leadtimeAsFloat
            }
        }
        return leadtime
    }

    get allResources(): Resource[] {
        return this.resources
            .reduce((acc, val) => acc.concat(val), [])
            .map((a) => a.resource)
            .filter((r): r is Resource => !!r)
    }

    setRateFromLines() {
        let total: number = 0
        for (let line of this.lines) {
            if (line.total && line.total > 0) total += line.total
            else if (line.rate && line.rate > 0) total += line.rate * (line.quantity || 1)
        }
        this.rate = total - (this.deposit || 0)
    }

    get package(): Package | undefined {
        return this.aPackage
    }

    set package(aPackage: Package | undefined) {
        this.aPackage = aPackage
    }

    async fields(): Promise<Field[]> {
        return await RecordFactory.getInstance().loadBookingFields(
            this.owner!.id!,
            false,
            PSApplication.role() === User.PERM_ROLE_CLIENT
        )
    }

    get endDate(): moment.Moment | undefined {
        if (this.item && this.item.type === 'reservation') {
            return this._endDate
        }
        return moment(this.date).add(this.duration || 0, 'minutes')
    }

    // todo: implement Conference.allowJoin()
    // get allowJoin(): boolean {
    //     return Conference.allowJoin(this, PSApplication.me())
    // }

    // todo implement Job.allowCheckin()
    // get allowCheckin(): boolean {
    //     let job = this.job
    //     let checkedInAndOut = job && job.checkinDate && job.checkoutDate
    //     return job && Job.allowCheckin(job, PSApplication.me()) && !checkedInAndOut
    // }

    get allowNotify(): boolean {
        return !!this.channel
    }

    get hasSchedule(): boolean {
        return !!this.schedule && !!this.schedule.createdDate
    }

    get isRecurring(): boolean {
        return !!this.schedule && (this.schedule.interval ?? 0) > 0
    }

    get scheduleStates(): ScheduleState[] {
        let isPayable =
            this.confirmedDate !== undefined &&
            this.paidDate === undefined &&
            this.invoicedDate === undefined &&
            moment().isAfter(this.date)
        let isCancelable = this.confirmedDate !== undefined && this.completedDate === undefined
        let isConfirmable = this.confirmedDate === undefined

        let states: ScheduleState[] = []
        if (isPayable && (this.rate === undefined || (this.amount && this.amount > 0)))
            states.push(ScheduleState.Chargeable)
        else if (isPayable) states.push(ScheduleState.Payable)
        else if (isCancelable) states.push(ScheduleState.Cancelable)
        else if (isConfirmable) states.push(ScheduleState.Confirmable)
        else states.push(ScheduleState.Complete)

        return states
    }

    set endDate(endDate: moment.Moment | undefined) {
        if (this.item && this.item.type === 'reservation') this._endDate = endDate
        else this.duration = endDate ? endDate.diff(this.date, 'minutes') : undefined
    }

    isLate(hours: number): boolean {
        let start = this.date
        let lateDate = moment(start).subtract(hours, 'hours')
        return this.status === 'confirmed' && moment().isAfter(lateDate)
    }

    get shouldShowEnforceCheckboxForCancellation(): boolean {
        if (this.vendor === undefined) {
            return false
        }

        const isLate =
            this.vendor.cancellationDuration !== undefined &&
            this.isLate(this.vendor.cancellationDuration)
        const hasPackage = this.package !== undefined
        const hasCancellationCharge =
            this.vendor.cancellationRate !== undefined &&
            (this.vendor.cancellationRateType === 'rate'
                ? this.vendor.cancellationRate >= 1
                : this.vendor.cancellationRateType === 'percentage'
                  ? this.vendor.cancellationRate * (this.total ?? 0) >= 1
                  : false)

        return isLate && (hasCancellationCharge || hasPackage)
    }

    get items(): Item[] {
        let items: Item[] = []
        for (let line of this.lines) {
            let item = line.item
            if (item) items.push(item)
        }
        return items
    }

    get services(): Item[] {
        return this.serviceLines.map((line) => line.item!)
    }

    get products(): Item[] {
        return this.productLines.map((line) => line.item!)
    }

    get serviceLines(): Line[] {
        return this.lines.filter((line) => !!line.item && line.item?.type !== 'product')
    }

    get productLines(): Line[] {
        return this.lines.filter((line) => line.item?.type === 'product')
    }

    get waitListed(): boolean {
        return this.status === 'waitListed'
    }

    get statusString(): string {
        if (this.id) {
            if (this.markPaidDate && this.paymeth)
                return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')} - ${this.paymeth}`
            if (this.markPaidDate) return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')}`
            if (this.paidDate) return `Paid ${this.paidDate.format('MMM D, YYYY')}`
            if (this.invoicedDate) return `Invoiced ${this.invoicedDate.format('MMM D, YYYY')}`
            if (this.cancelledDate) return `Canceled ${this.cancelledDate.format('MMM D, YYYY')}`
            if (this.checkoutDate) return `Checked out ${this.checkoutDate.format('MMM D, YYYY')}`
            if (this.checkinDate) return `Checked in ${this.checkinDate.format('MMM D, YYYY')}`
            if (this.completedDate) return `Completed ${this.completedDate.format('MMM D, YYYY')}`
            if (this.confirmedDate && this.paidDepositDate)
                return `Paid deposit ${this.confirmedDate.format('MMM D, YYYY')}`
            if (this.confirmedDate) return `Confirmed ${this.confirmedDate.format('MMM D, YYYY')}`
            if (this.status === 'confirmed') return 'Confirmed'
            if (this.status === 'sentQuote' && this.confirmStatus === 'confirmPayment')
                return 'Requires credit card'
            if (this.status === 'sentQuote') return 'Requires confirmation'
            if (this.waitListed)
                return `Joined wait list ${this.createdDate!.format('MMM D, YYYY')}`
            if (this.online) return `Requested online ${this.createdDate!.format('MMM D, YYYY')}`
            if (this.id) return `Requested ${this.createdDate!.format('MMM D, YYYY')}`
            return 'Requires approval'
        }
        else {
            return this.editStatusString
        }
    }

    get editStatusString(): string {
        if (this.status === 'confirmed')
            return this.paidDepositDate ? 'Confirmed (paid deposit)' : 'Already confirmed'
        else if (this.status === 'sentQuote' && this.confirmStatus === 'confirmPayment')
            return 'Requires credit card'
        else if (this.status === 'sentQuote') return 'Requires confirmation'
        if (!this.id && ['package', 'subscription', 'gift'].includes(this.item?.type ?? ''))
            return this.total ? 'Pending payment' : 'Pending confirmation'
        if (this.waitListed) return this.id ? 'Joined wait list' : 'Join wait list'
        if (
            this.status === 'sentRequest' &&
            this.vendor?.bookingStatus === 'confirmed' &&
            PSApplication.me().id === this.client?.id
        )
            return this.item?.type === 'class' ? 'Book class' : 'Confirm appointment'
        return this.item?.type === 'class' ? 'Request class' : 'Request appointment'
    }

    get statuses(): {
        title: string
        date: moment.Moment | undefined
    }[] {
        if (this.lesson) {
            return [
                { title: 'Sent', date: this.sentDate },
                { title: 'Viewed', date: this.viewedDate },
                { title: 'Registered', date: this.confirmedDate },
                { title: 'Checked in', date: this.checkinDate },
            ]
        }

        if (this.package) {
            return [
                { title: 'Created', date: this.createdDate },
                { title: 'Sent', date: this.sentDate },
                { title: 'Confirmed', date: this.confirmedDate },
            ]
        }

        return [
            { title: 'Created', date: this.createdDate },
            { title: 'Sent', date: this.sentDate },
            { title: 'Confirmed', date: this.confirmedDate },
            { title: 'Complete', date: this.completedDate || this.paidDate },
        ]
    }

    get statusTag(): { label: string; intent: TagProps['intent'] } {
        switch (this.status) {
            case 'cancelled':
                return { label: 'Canceled', intent: 'danger' }
            case 'completed':
                return { label: 'Completed', intent: 'success' }
            case 'confirmed':
                return { label: 'Confirmed', intent: 'success' }
            case 'sentQuote':
                return { label: 'Sent', intent: 'warning' }
            case 'sentRequest':
                return { label: 'Sent', intent: 'warning' }
            case 'waitListed':
                return { label: 'Waitlisted', intent: 'warning' }
            default:
                return { label: 'Pending', intent: 'warning' }
        }
    }

    get job(): Job | undefined {
        let firstJob: Job | undefined = undefined
        for (let job of this.jobs) {
            firstJob = firstJob ?? job
            if (job.person?.id === PSApplication.me().id) return job
        }
        return PSApplication.role() === User.PERM_ROLE_CONTRACTOR ||
            PSApplication.role() === User.PERM_ROLE_EMPLOYEE
            ? undefined
            : firstJob
    }

    get jobCheckinDate(): moment.Moment | undefined {
        for (let job of this.jobs) if (job.checkinDate) return job.checkinDate
        return undefined
    }

    get jobCheckinStatus(): string | undefined {
        let late = false
        let good = true
        for (let job of this.jobs) {
            const jobStatus = job.checkinStatus
            if (jobStatus === 'NO SHOW') return jobStatus
            late = late || jobStatus === 'LATE'
            good = good && jobStatus === 'ON TIME'
        }
        return good ? 'ON TIME' : late ? 'LATE' : undefined
    }

    get jobCheckoutDate(): moment.Moment | undefined {
        for (let job of this.jobs) if (job.checkoutDate) return job.checkoutDate
        return undefined
    }

    toString(): string {
        if (this.location) return this.location
        return this.title
    }

    getLocation(): string | undefined {
        return Person.locationString(
            this.line1,
            this.line2,
            this.city,
            this.state,
            this.postalCode,
            this.location
        )
    }

    get coordinate(): [number, number] | undefined {
        if (this.latitude !== undefined && this.longitude !== undefined)
            return [this.latitude, this.longitude]
        return undefined
    }

    setAddressFromSubaccount(subaccount: Subaccount) {
        if (
            subaccount.line1 &&
            (!this.vendor?.vendorLocation || (this.item && this.item.clientSelectsLocation)) &&
            (!this.item || !this.item.location) &&
            (!this.event || !this.event.location)
        ) {
            this.line1 = subaccount.line1
            this.line2 = subaccount.line2
            this.city = subaccount.city
            this.state = subaccount.state
            this.postalCode = subaccount.postalCode
        }
    }

    get title(): string {
        const lesson = this.lesson
        if (lesson && this.client?.id === PSApplication.me().id) return this.item?.name!
        const person = this.person
        if (person && this.item) return `${person.name} - ${this.item.name}`
        else if (person) return `${person.name}`
        else if (this.item) return `${this.item.name}`
        return `Appointment - ${this.statusString} (PocketSuite)`
    }

    get shortTitle(): string {
        const lesson = this.lesson
        if (lesson && this.client?.id === PSApplication.me().id) return this.item!.name!
        const person = this.person
        if (person) return person.name!
        return `Appointment - ${this.statusString} (PocketSuite)`
    }

    get total(): number | undefined {
        const rate = this.rate
        const deposit = this.deposit
        if (rate !== undefined && deposit !== undefined) return rate + deposit
        return rate ?? deposit
    }

    get amount(): number | undefined {
        if (this.discount && this.discount > 0) return (this.total ?? 0) - this.discountAmount
        return this.total
    }

    get gratuityAmount(): number {
        if (this.gratuity && this.gratuity !== 0) {
            let gratuity = this.gratuity
            if (this.gratuityType === 'percent')
                if (this.amount === undefined) gratuity = 0
                else gratuity = this.gratuity * currencyFloat(this.amount / 100)
            return gratuity
        }
        return 0
    }

    get surchargeAmount(): number {
        if (this.allowSurcharge && !this.markPaidDate)
            return (
                ((this.amount ?? 0) + this.gratuityAmount + this.taxAmount) *
                Payment.PAYMENT_SURCHARGE_RATE
            )
        return 0
    }

    get chargeAmount(): number {
        return (this.amount ?? 0) + this.taxAmount + this.gratuityAmount + this.surchargeAmount
    }

    get askingAmount(): number {
        return (this.amount ?? 0) + this.taxAmount
    }

    get depositTaxAmount(): number {
        const taxcode = this.taxcode
        let taxableAmount = taxcode ? this.owedAmount : 0
        if (this.lines?.length > 0 && taxcode) {
            taxableAmount = 0
            for (let line of this.lines)
                if (line.amount! > 0 && line.taxable) taxableAmount += line.amount!
        }
        const deposit = this.deposit!
        if (deposit > 0) taxableAmount = deposit <= taxableAmount ? deposit : taxableAmount
        if (taxableAmount > 0)
            return this.tax && this.tax > 0 ? this.tax * currencyFloat(taxableAmount * 0.01) : 0
        return 0
    }

    get depositChargeAmount(): number {
        const amount = this.deposit!
        if (amount > 0) {
            const taxAmount = this.tax && this.tax > 0 ? this.tax * amount * 0.01 : 0
            const surchargeAmount = this.allowSurcharge
                ? (taxAmount + amount) * Payment.PAYMENT_SURCHARGE_RATE
                : 0
            return amount + taxAmount + surchargeAmount
        }
        return 0
    }

    get finalSurchargeAmount(): number {
        const total = this.amount ?? 0
        const amount = this.owedAmount
        const taxAmount = this.finalTaxAmount
        const gratuityAmount =
            this.gratuityType === 'percent'
                ? currencyFloat(((this.gratuity ?? 0) * total) / 100)
                : (this.gratuity ?? 0)
        const surchargeAmount = this.allowSurcharge
            ? (amount + gratuityAmount + taxAmount) * Payment.PAYMENT_SURCHARGE_RATE
            : 0
        return surchargeAmount
    }

    get finalChargeAmount(): number {
        const total = this.amount ?? 0
        const amount = this.owedAmount
        const taxAmount = this.finalTaxAmount
        const gratuityAmount =
            this.gratuityType === 'percent'
                ? currencyFloat(((this.gratuity ?? 0) * total) / 100)
                : (this.gratuity ?? 0)
        const surchargeAmount = this.allowSurcharge
            ? (amount + gratuityAmount + taxAmount) * Payment.PAYMENT_SURCHARGE_RATE
            : 0
        return amount + taxAmount + gratuityAmount + surchargeAmount
    }

    get finalAskingAmount(): number {
        const amount = this.owedAmount
        const taxAmount = this.finalTaxAmount
        return amount + taxAmount
    }

    get taxableAmount(): number {
        const taxcode = this.taxcode
        if (this.lines?.length > 0 && taxcode) {
            let total = 0
            for (let line of this.lines)
                if (line.amount && line.amount > 0 && line.taxable) total += line.amount
            return total
        }
        return taxcode ? (this.amount ?? 0) : 0
    }

    get taxAmount(): number {
        const taxableAmount = this.taxableAmount
        if (taxableAmount > 0) {
            if (this.tax && this.tax > 0) return this.tax * (taxableAmount ?? 0) * 0.01
            else if (this.vendor?.taxcode && this.vendor.taxcode.rate! > 0)
                return this.vendor.taxcode.rate! * currencyFloat((taxableAmount ?? 0) * 0.01)
        }
        return 0
    }

    get finalTaxAmount(): number {
        const taxcode = this.taxcode
        let taxableAmount = taxcode ? this.owedAmount : 0
        if (this.lines?.length > 0 && taxcode) {
            taxableAmount = 0
            for (let line of this.lines)
                if (line.amount && line.amount > 0 && line.taxable) taxableAmount += line.amount
        }
        if (taxableAmount > 0) {
            const taxRate =
                this.tax && this.tax > 0
                    ? this.tax
                    : this.vendor?.taxcode?.rate && this.vendor.taxcode.rate > 0
                      ? this.vendor?.taxcode.rate
                      : null
            if (taxRate) {
                const taxAmount = currencyFloat(taxRate * taxableAmount * 0.01)
                return this.paidDepositTax && this.paidDepositTax > 0
                    ? Math.max(0.0, taxAmount - this.paidDepositTax)
                    : taxAmount
            }
        }
        return 0
    }

    get discountAmount(): number {
        if (this.discount && this.discount > 0) {
            let discount = this.discount
            const total = this.discountableTotal
            if (this.discountType === 'percent') discount = this.discount * (total ?? 0) * 0.01
            else if (discount > total) discount = total
            return discount!
        }
        return 0
    }

    get discountableTotal(): number {
        if (this.lines?.length > 0) {
            let total = 0
            for (let line of this.lines) if (line.discountable && line.total) total += line.total
            return total
        }
        return this.total ?? 0
    }

    get paidAmount(): number {
        if (!this.markPaidDate) return 0
        let amount = this.amount ?? 0
        const deposit = this.deposit
        if (deposit && deposit > 0 && this.paidDepositDate) amount -= deposit
        if (this.gratuityAmount > 0) amount += this.gratuityAmount
        if (this.taxAmount > 0) amount += this.taxAmount
        return amount
    }

    get owedAmount(): number {
        if (this.paidDate) return 0
        let amount = this.amount ?? 0
        const deposit = this.deposit
        if (deposit && deposit > 0 && this.paidDepositDate) amount -= deposit
        return amount!
    }

    get when(): string {
        let dateString: string
        const duration = this.duration
        if (duration && duration > 0) dateString = this.date!.format('M/D/YY')
        else
            dateString = `${this.date!.format('M/D/YY')}, ${this.date!.format('h:mma').toLowerCase()}`
        return dateString
    }

    get fixedScheduleCount(): number {
        const deposit = this.deposit
        const schedule = this.schedule
        if (
            schedule &&
            (!deposit || deposit === 0) &&
            ((schedule.instances && schedule.instances > 1) || schedule.dateValues!.length > 0)
        )
            return schedule.dateValues!.length > 1
                ? schedule.dateValues!.length
                : schedule.instances!
        return 0
    }

    get fixedScheduleTotal(): number | undefined {
        const total = this.total
        if (!total || total === 0) return undefined
        return this.fixedScheduleCount * total
    }

    get fixedScheduleAskingAmount(): number | undefined {
        const askingAmount = this.askingAmount
        if (!askingAmount || askingAmount === 0) return undefined
        return this.fixedScheduleCount * askingAmount
    }

    // Bookable interface methods
    get color(): string | BookableColorKey | undefined {
        return !!this.item
            ? this.item.color
            : this.type === 'service'
              ? getDefaultServiceColorKey()
              : undefined
    }

    allProductStringAndCount(limit?: number): string {
        const strings: string[] = []
        const productLines = this.productLines
        for (const line of productLines) {
            if (!limit || strings.length < limit) {
                const item_display = line.item!.name
                const quantity = line.quantity
                if (item_display) strings.push(`${item_display} x${quantity}`)
            }
        }
        if (productLines.length > strings.length) strings.push('...')
        return strings.join(', ')
    }

    get allowJoin(): boolean {
        return false
    }

    get allowCheckin(): boolean {
        return (
            this.type === 'reservation' &&
            !!this.confirmedDate &&
            !this.cancelledDate &&
            !this.checkinDate &&
            !!this.reserveDate &&
            moment().isAfter(this.reserveDate) &&
            !!this.reserveEndDate &&
            moment().isBefore(this.reserveEndDate.add(1, 'days'))
        )
    }

    get allowUndoCheckin(): boolean {
        return (
            this.type === 'reservation' &&
            !!this.confirmedDate &&
            !this.cancelledDate &&
            !!this.checkinDate &&
            !this.checkoutDate
        )
    }

    get allowCheckout(): boolean {
        return (
            this.type === 'reservation' &&
            !!this.confirmedDate &&
            !this.cancelledDate &&
            !!this.checkinDate &&
            !this.checkoutDate
        )
    }

    get allowUndoCheckout(): boolean {
        return this.type === 'reservation' && !this.cancelledDate && !!this.checkoutDate
    }

    get allowAutoAssign(): boolean {
        return !this.cancelledDate && !this.completedDate && this.jobs.length === 0
    }

    get checkinStartDate(): moment.Moment | null {
        if (this.reserveCheckinType === null) {
            return null
        }

        switch (this.reserveCheckinType) {
            case 'any': {
                if (this.item === undefined || this.owner === undefined) {
                    return null
                }

                const baseDate = this.reserveDate ?? this.date
                if (baseDate === undefined) {
                    return null
                }

                const startHour =
                    this.item?.startHourOnDate(baseDate) ?? this.owner?.startHourOnDate(baseDate)
                if (startHour === undefined || startHour <= 0) {
                    return baseDate
                }

                return baseDate.add(startHour * 60, 'minutes')
            }
            case 'fixed':
            case 'flex': {
                return this.date ?? null
            }
        }
    }

    get checkinEndDate(): moment.Moment | null {
        if (this.reserveCheckinType === null) {
            return null
        }

        switch (this.reserveCheckinType) {
            case 'any': {
                if (this.item === undefined || this.owner === undefined) {
                    return null
                }

                const baseDate = this.reserveDate ?? this.date
                if (baseDate === undefined) {
                    return null
                }

                const endHour =
                    this.item?.endHourOnDate(baseDate) ?? this.owner?.endHourOnDate(baseDate)
                if (endHour === undefined || endHour <= 0) {
                    return baseDate
                }

                return baseDate.add(endHour * 60, 'minutes')
            }
            case 'fixed':
                return null
            case 'flex': {
                if (this.date === undefined || (this.reserveCheckinDuration ?? 0) <= 0) {
                    return null
                }
                return this.date.add(this.reserveCheckinDuration, 'minutes')
            }
        }
    }

    get checkinTimeDescription(): string | null {
        if (this.reserveCheckinType === null) {
            return null
        }

        if (this.reserveCheckinType === 'any') {
            return 'Any time'
        }

        if (!this.checkinStartDate) {
            return null
        }

        if (!this.checkinEndDate) {
            return this.checkinStartDate.format('h:mm A')
        }

        if (this.checkinStartDate.format('A') === this.checkinEndDate.format('A')) {
            return `${this.checkinStartDate.format('h:mm')} - ${this.checkinEndDate.format('h:mm A')}`
        }
        else {
            return `${this.checkinStartDate.format('h:mm A')} - ${this.checkinEndDate.format('h:mm A')}`
        }
    }

    get checkoutStartDate(): moment.Moment | null {
        if (this.reserveCheckoutType === null) {
            return null
        }

        switch (this.reserveCheckoutType) {
            case 'any': {
                if (this.item === undefined || this.owner === undefined) {
                    return null
                }

                const baseDate = this.reserveEndDate ?? this.endDate
                if (baseDate === undefined) {
                    return null
                }

                if (this.item.checkoutStartTime !== null && this.item.checkoutStartTime > 0) {
                    return baseDate.add(this.item.checkoutStartTime * 60, 'minutes')
                }
                else {
                    const ownerStartHour = this.owner.startHourOnDate(baseDate)
                    if (ownerStartHour === undefined || ownerStartHour <= 0) {
                        return baseDate
                    }
                    return baseDate.add(ownerStartHour * 60, 'minutes')
                }
            }
            case 'fixed':
            case 'flex': {
                return this.endDate ?? null
            }
        }
    }

    get checkoutEndDate(): moment.Moment | null {
        if (this.reserveCheckoutType === null) {
            return null
        }

        switch (this.reserveCheckoutType) {
            case 'any': {
                if (this.item === undefined || this.owner === undefined) {
                    return null
                }

                const baseDate = this.reserveEndDate ?? this.endDate
                if (baseDate === undefined) {
                    return null
                }

                if (this.item.checkoutEndTime !== null && this.item.checkoutEndTime > 0) {
                    return baseDate.add(this.item.checkoutEndTime * 60, 'minutes')
                }
                else {
                    const ownerEndHour = this.owner.endHourOnDate(baseDate)
                    if (ownerEndHour === undefined || ownerEndHour <= 0) {
                        return baseDate
                    }
                    return baseDate.add(ownerEndHour * 60, 'minutes')
                }
            }
            case 'fixed':
                return null
            case 'flex': {
                if (this.endDate === undefined || (this.reserveCheckoutDuration ?? 0) <= 0) {
                    return null
                }
                return this.endDate.add(this.reserveCheckoutDuration, 'minutes')
            }
        }
    }

    get checkoutTimeDescription(): string | null {
        if (this.reserveCheckoutType === null) {
            return null
        }

        if (this.reserveCheckoutType === 'any') {
            return 'Any time'
        }

        if (!this.checkoutStartDate) {
            return null
        }

        if (!this.checkoutEndDate) {
            return this.checkoutStartDate.format('h:mm A')
        }

        if (this.checkoutStartDate.format('A') === this.checkoutEndDate.format('A')) {
            return `${this.checkoutStartDate.format('h:mm')} - ${this.checkoutEndDate.format('h:mm A')}`
        }
        else {
            return `${this.checkoutStartDate.format('h:mm A')} - ${this.checkoutEndDate.format('h:mm A')}`
        }
    }

    get ownerIsMe(): boolean {
        return this.owner != undefined && this.owner.id === PSApplication.me().id
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.date) this.date = moment(data.date)
        if (data.endDate) this.endDate = moment(data.endDate)
        if (data.service) this.service = BaseRecord.fromSerializer(data.service, Industry)
        if (data.estimate) this.estimate = BaseRecord.fromSerializer(data.estimate, Estimate)
        if (data.fieldValues) this.fieldValues = new RecordDataMap(data.fieldValues)
        if (data.gift) this.gift = BaseRecord.fromSerializer(data.gift, Gift)
        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.promotion) this.promotion = BaseRecord.fromSerializer(data.promotion, Promotion)
        if (data.item) this.item = BaseRecord.fromSerializer(data.item, Item)
        if (data.product) this.product = BaseRecord.fromSerializer(data.product, Item)
        if (data.lesson) this.lesson = BaseRecord.fromSerializer(data.lesson, Lesson)
        if (data.aPackage) this.aPackage = BaseRecord.fromSerializer(data.aPackage, Package)
        if (data.event) this.event = BaseRecord.fromSerializer(data.event, PSEvent)
        if (data.checkinBy) this.checkinBy = BaseRecord.fromSerializer(data.checkinBy, User)
        if (data.dates?.length) this.dates = data.dates.map((date: any) => moment(date))
        if (data.lessons) {
            this.lessons = data.lessons.map((lesson: any) =>
                BaseRecord.fromSerializer(lesson, Lesson)
            )
        }
        if (data.employees) {
            this.employees = data.employees.map((eList: any[]) =>
                eList.map((e: any) => BaseRecord.fromSerializer(e, User))
            )
        }
        if (data.resources) {
            this.resources = data.resources.map((rList: any[]) =>
                rList.map((r: any) => BaseRecord.fromSerializer(r, Allocation))
            )
        }
        if (data.schedule) this.schedule = BaseRecord.fromSerializer(data.schedule, Schedule)
        if (data.customer) this.customer = BaseRecord.fromSerializer(data.customer, User)
        if (data.markPaidDate) this.markPaidDate = moment(data.markPaidDate)
        if (data.invoicedDate) this.invoicedDate = moment(data.invoicedDate)
        if (data.rebookDate) this.rebookDate = moment(data.rebookDate)
        if (data.paidDepositDate) this.paidDepositDate = moment(data.paidDepositDate)
        if (data.checkinDate) this.checkinDate = moment(data.checkinDate)
        if (data.completedDate) this.completedDate = moment(data.completedDate)
        if (data.confirmedDate) this.confirmedDate = moment(data.confirmedDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data.packageCancelledDate) this.packageCancelledDate = moment(data.packageCancelledDate)
        if (data.jobs) this.jobs = data.jobs.map((job: any) => BaseRecord.fromSerializer(job, Job))
        if (data.allocations) {
            this.allocations = data.allocations.map((allocation: any) =>
                BaseRecord.fromSerializer(allocation, Allocation)
            )
        }
    }

    static fk: FKType = mergeFk(Transaction.fk, {
        industries: ['service'],
        estimates: ['estimate'],
        gifts: ['gift'],
        sources: ['source'],
        promotions: ['promotion'],
        items: ['item', 'product'],
        lessons: ['lesson'],
        packages: ['aPackage'],
        events: ['event'],
        users: ['checkinBy', 'customer'],
        schedules: ['schedule'],
    })

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['date'] = formatTimeStamp(this.date)
        if (this.item && this.item.type === 'reservation')
            dict['end_date'] = formatTimeStamp(this.endDate)
        dict['rate'] = this.rate ?? ''
        dict['deposit'] = this.deposit && this.deposit > 0 ? this.deposit : ''
        dict['estimate'] = this.estimate?.id ?? ''
        dict['duration'] = this.duration ?? ''
        dict['discount'] = this.discount && this.discount > 0 ? this.discount : ''
        dict['discount_type'] =
            this.discount && this.discount > 0 && this.discountType ? this.discountType : ''
        dict['gratuity'] = this.gratuity ?? ''
        dict['gratuity_type'] =
            this.gratuity && this.gratuity > 0 && this.gratuityType ? this.gratuityType : ''
        dict['is_dropin'] = this.dropin
        dict['allow_surcharge'] = this.allowSurcharge
        dict['allow_conference'] = this.allowConference
        dict['conference_type'] = this.conferenceType
        if (this.customer) dict['customer'] = this.customer.id
        dict['line1'] = this.line1 ?? ''
        dict['line2'] = this.line2 ?? ''
        dict['city'] = this.city ?? ''
        dict['state'] = this.state ?? ''
        dict['postal_code'] = this.postalCode ?? ''
        dict['country_code'] = this.countryCode ?? ''
        dict['event'] = this.event?.id ?? ''
        dict['status'] = this.status ?? ''
        dict['rebook'] = this.rebook ?? ''
        dict['cancellation_type'] = this.cancellationType ?? ''
        dict['cancellation_memo'] = this.cancellationMemo ?? ''
        dict['checkout'] = this.checkout ?? ''
        dict['confirm_status'] = this.confirmStatus ?? ''
        dict['item'] = this.item?.id ?? ''
        dict['gift'] = this.gift?.id ?? ''
        dict['service'] = this.service?.id ?? ''
        dict['promotion'] = this.promotion?.id ?? ''
        dict['field_values'] = this.fieldValues
            ? Field.serializeFieldData(this.fieldValues, this.vendor?.id, undefined)
            : ''
        if (this.lesson) dict['lesson'] = this.lesson.id
        dict['memo'] = this.memo ?? ''
        dict['instructions'] = this.instructions ?? ''
        if (this.aPackage && this.aPackage.id) dict['package'] = this.aPackage.id
        if (this.source) dict['source'] = this.source.id
        const jsonJobs = this.jobs?.map((job: Job) => job.jsonObject()) || []
        dict['jobs'] = jsonJobs
        const jLines = this.lines?.map((line: Line) => line.jsonObject()) || []
        dict['lines'] = jLines
        const jsonAllocations =
            this.allocations?.map((allocation: Allocation) => allocation.jsonObject()) || []
        dict['allocations'] = jsonAllocations
        if (this.forSchedule && this.schedule) {
            if (this.schedule.id) dict['schedule'] = this.schedule.id
            dict['instances'] = this.schedule.instances ?? ''
            if (this.schedule.interval && this.schedule.interval > 0) {
                dict['dow'] = this.schedule.dow
                dict['interval'] = this.schedule.interval
                dict['frequency'] = this.schedule.frequency
                if (this.schedule.wom && this.schedule.wom !== 0) dict['wom'] = this.schedule.wom
            }
            else {
                dict['interval'] = ''
            }
        }
        return dict
    }

    static dataType(): DataType {
        return 'bookings'
    }

    get lesson(): Lesson | undefined {
        return this.getForeignRecord('lesson', Lesson)
    }
    set lesson(lesson: Lesson | undefined) {
        this.setForeignRecord('lesson', lesson)
    }
    get aPackage(): Package | undefined {
        return this.getForeignRecord('package', Package)
    }
    set aPackage(aPackage: Package | undefined) {
        this.setForeignRecord('package', aPackage)
    }
    get promotion(): Promotion | undefined {
        return this.getForeignRecord('promotion', Promotion)
    }
    set promotion(promotion: Promotion | undefined) {
        this.setForeignRecord('promotion', promotion)
    }
    get gift(): Gift | undefined {
        return this.getForeignRecord('gift', Gift)
    }
    set gift(gift: Gift | undefined) {
        this.setForeignRecord('gift', gift)
    }
    get item(): Item | undefined {
        return this.getForeignRecord('item', Item)
    }
    set item(item: Item | undefined) {
        this.setForeignRecord('item', item)
    }
    get schedule(): Schedule | undefined {
        return this.getForeignRecord('schedule', Schedule)
    }
    set schedule(schedule: Schedule | undefined) {
        this.setForeignRecord('schedule', schedule)
    }
    get service(): Industry | undefined {
        return this.getForeignRecord('service', Industry)
    }
    set service(service: Industry | undefined) {
        this.setForeignRecord('service', service)
    }
    get estimate(): Estimate | undefined {
        return this.getForeignRecord('estimate', Estimate)
    }
    set estimate(estimate: Estimate | undefined) {
        this.setForeignRecord('estimate', estimate)
    }
    get checkinBy(): User | undefined {
        return this.getForeignRecord('checkin_by', User)
    }
    set checkinBy(checkinBy: User | undefined) {
        this.setForeignRecord('checkin_by', checkinBy)
    }
    get product(): Item | undefined {
        return this._product ?? this.products[0]
    }
    set product(product: Item | undefined) {
        this._product = product
    }
    get source(): Source | undefined {
        return this.getSourceRecord('source')
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
}
